import * as React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { Stack, Typography } from '@mui/material';
import Slide from '@mui/material/Slide';
import ApiService from "services/ApiService";
import { ToFeatures } from 'services/GeoJsonService';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/PriorityHigh';
import defColors from 'utils/defColors';
import { ChipStack } from 'pages/components/ChipStack';
import { repeatEffectSeq } from 'utils/commonFunc';
import { getFacilityTypeName } from 'utils/commonData';
import BaseMapView from 'map/BaseMapView';
import CommonBoundaryLayer from 'map/layers/CommonBoundaryLayer';
import CommonMarkerLayer from 'map/layers/CommonMarkerLayer';
import { CacheStyleManger } from 'map/OlExtension';
import { color } from '@amcharts/amcharts5';
import { px } from 'framer-motion';

const StatusInfoBox = ({ valid, count }) => {
    const iconDef = {
        sx: {
            transform: 'scale(2.0)',
            mr: 3,
        }
    }
    return (
        <Paper elevation={3} sx={{ p: 3, backgroundColor: 'var(--white-05)', border: '1px solid var(--white-20)', height: '220px', backgroundImage: 'none', borderRadius: '20px', alignItems: 'center', }}>
            <Stack
                justifyContent="center"
                //alignItems="center"
                spacing={2}
                sx={{ width: '100%', height: '100%' }}
            >
                <Paper variant="outlined" sx={{ position: 'relative', p: 1.5, pl: 4, pr: 4, width: '100%', backgroundColor: defColors.validColorFunc(valid), border: '3px solid', borderColor: defColors.validColorFuncBd(valid), borderRadius: '10px' }}>
                    <Typography variant="h4">{valid ? <CheckIcon {...iconDef} /> : <ErrorIcon  {...iconDef} />}{valid ? '정상' : '장애'}</Typography>
                </Paper>
                <Typography variant='h3' textAlign={'center'} display={'flex'} alignItems={'flex-end'} justifyContent={'center'} p={2} sx={{ fontWeight: 500, fontSize: '56px', color: defColors.validColorFuncBd(valid) }} >{count ? count : '0'}<span style={{ fontSize: '32px', marginLeft: '8px', marginBottom: '6px', color: 'var(--white-80)' }}>건</span></Typography>
            </Stack>
        </Paper>
    )
}

const dummyStatusData = { in: false, index: 0, typeName: undefined, validCount: 0, unvalidCount: 0, items: [] }

const mockFacilityStatusData = [
    {
        facilityType: "03", // VDS
        valid: 1,
        unvalid: 0,
        total: 1,
        items: [
            {
                id: "VD01A00001",
                location: "서울역사거리",
                comm: true,
                date: "2025-05-23T09:00:00",
                x: 126.9723,
                y: 37.5547
            }
        ]
    },
    {
        facilityType: "07", // CCTV
        valid: 0,
        unvalid: 1,
        total: 1,
        items: [
            {
                id: "CC07B00001",
                location: "강남역사거리",
                comm: false,
                date: "2025-05-23T09:10:00",
                x: 127.0276,
                y: 37.4979
            }
        ]
    },
    {
        facilityType: "08", // VMS
        valid: 1,
        unvalid: 0,
        total: 1,
        items: [
            {
                id: "VM08C00001",
                location: "서초IC",
                comm: true,
                date: "2025-05-23T09:15:00",
                x: 127.015,
                y: 37.482
            }
        ]
    },
    {
        facilityType: "12", // 영상VDS
        valid: 0,
        unvalid: 1,
        total: 1,
        items: [
            {
                id: "VD12D00001",
                location: "신림역사거리",
                comm: false,
                date: "2025-05-23T09:20:00",
                x: 126.9292,
                y: 37.4849
            }
        ]
    },
    {
        facilityType: "17", // DSRC
        valid: 1,
        unvalid: 0,
        total: 1,
        items: [
            {
                id: "DS17E00001",
                location: "연신내역",
                comm: true,
                date: "2025-05-23T09:25:00",
                x: 126.9185,
                y: 37.6191
            }
        ]
    },
    {
        facilityType: "80", // 스마트교차로
        valid: 0,
        unvalid: 1,
        total: 1,
        items: [
            {
                id: "SW80F00001",
                location: "장안구청사거리",
                comm: false,
                date: "2025-05-23T09:30:00",
                x: 127.0093,
                y: 37.3015
            }
        ]
    },
];



const chipStyleFms = {
    position: 'absolute',
    top: '-130px',
    right: '0',
    zIndex: '10',
};

export default function FacilityStatusView() {
    const mapRef = React.useRef();


    const [pageIndex, setPageIndex] = React.useState(0); // 현재 페이지 인덱스 (0부터 시작)
    const itemsPerPage = 3; // 한 번에 보여줄 아이템 수

    const [statusChipList, setStatusChipList] = React.useState([]);
    const [selectedStatusData, setSelectedStatusData] = React.useState(dummyStatusData);
    // React.useEffect(() => {
    //     const interval = setInterval(() => {
    //         setPageIndex(prev => {
    //             const maxPage = Math.ceil(statusChipList.length / itemsPerPage);
    //             return (prev + 1) % maxPage;
    //         });
    //     }, 15000); // 15초마다 페이지 전환

    //     return () => clearInterval(interval); // cleanup
    // }, [statusChipList.length]);

    React.useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current.GetOlMap();
            map.addLayer(new CommonBoundaryLayer(map));

            const layer = new CommonMarkerLayer(map)
            map.addLayer(layer);

            const styleManager = new CacheStyleManger()
            styleManager.addCreateCallBack('valid', () => layer.getDefaultStyle())
            styleManager.addCreateCallBack('unvalid', () => layer.getDefaultStyle({
                fillColor: "rgba(255, 60, 53, 0.7)",
                storkeColor: "rgba(255, 25, 25, 1)",
                zIndex: 2
            }))

            let statusDataItems = []

            return repeatEffectSeq(
                async (seq) => {
                    if (seq === 0) {
                        const response = await ApiService.get("newdashboard/roadtraffic/Status")  
                        if (response.status === 200) {
                        const data = response.data; 
                        //const data = mockFacilityStatusData;
                        statusDataItems = data.map(arr => ({
                            in: true,
                            items: arr.items, typeName: getFacilityTypeName(arr.facilityType),
                            validCount: arr.valid,
                            unvalidCount: arr.unvalid
                        }))
                        setStatusChipList(statusDataItems.map(p => p.typeName))
                        } 
                        else {
                            return 
                        }
                    }

                    const selected = statusDataItems[seq];
                    if (selected) {
                        setSelectedStatusData(selected)

                        var validStyle = styleManager.getStyles('valid', 1)[0]
                        var unvalidStyle = styleManager.getStyles('unvalid', 1)[0]
                        layer.update(
                            ToFeatures(selected.items, { typeName: selected.typeName })
                                .map(feature => {
                                    const properties = feature.get('properties');
                                    if (properties?.comm === true) {
                                        feature.setStyle(validStyle)
                                    } else {
                                        feature.setStyle(unvalidStyle)
                                        //todo unvalidStyle 에 setText를 위해서는 index 관리해서해야함
                                        //text: properties.location 
                                    }
                                    return feature
                                }))
                        layer.startAnimationImageScale([validStyle, unvalidStyle])
                    }

                    return statusDataItems.length;
                },
                5)
        }
    }, [mapRef]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'visible' }} title={'ITS 시설물 상태 모니터링'}>
            <div style={{ width: '100%', height: '100%', marginLeft: '-400px', position: 'relative' }}>
                <BaseMapView
                    ref={mapRef}
                    initViewData={{ zoom: 12.4, center: [127.01288, 37.290] }}
                />
            </div>
            <Box sx={{ position: 'absolute', bottom: 0, right: 0, height: '90%', width: '640px', pt: 6, pb: 6, pr: 4, pl: 4, borderRadius: '20px', alignItems: 'stretch', backgroundColor: defColors.innerBox[1], backgroundImage: 'none', }}>
                <ChipStack
                    textItems={statusChipList}
                    selectedText={selectedStatusData?.typeName}
                    sx={chipStyleFms}
                    chipSx={{
                        minWidth:'148px',
                        labelSx: {
                            fontSize: '22px', 
                            display:'flex',
                            alignItems:'center',
                            justifyContent:'center',
                            fontWeight: 600, 
                            }
                    }}
                />
                {/* <ChipStack
                    textItems={statusChipList.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)}
                    selectedText={selectedStatusData?.typeName}
                    sx={chipStyleFms}
                /> */}
                <Slide direction='left' in={selectedStatusData?.in} sx={{ height: '100%', width: '100%' }}>
                    <Stack sx={{ height: '100%', width: '100%' }}
                        direction="column"
                        justifyContent="center"
                        alignItems="stretch"
                        spacing={3}>

                        <Typography variant='h4' textAlign={'center'} sx={{ width: '100%', height: '100%', fontSize: '36px;', fontWeight: 500 }}>{selectedStatusData?.typeName} 상태</Typography>
                        <StatusInfoBox valid={true} count={selectedStatusData?.validCount} />
                        <StatusInfoBox valid={false} count={selectedStatusData?.unvalidCount} />
                    </Stack>
                </Slide>
            </Box>
        </div>
    )
}